import { motion } from "framer-motion";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import { localBusiness } from "@/lib/business";

export default function About() {

  return (
    <div>
      <SEO
        title="About Us"
        description="Aussie Inspections is a Darwin-based building and property inspection team serving Darwin, Palmerston, Litchfield and Wagait. Flat-rate pricing from $260 incl. GST, digital reports within 72 hours."
        keywords="about Aussie Inspections, building inspectors Darwin, property inspection company NT"
        url="/about"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "url": "https://aussieinspections.com.au/about",
          "mainEntity": localBusiness({
            areaServed: [
              { "@type": "City", "name": "Darwin" },
              { "@type": "City", "name": "Palmerston" },
              { "@type": "AdministrativeArea", "name": "Litchfield" },
              { "@type": "AdministrativeArea", "name": "Wagait" }
            ]
          })
        }}
      />

      {/* CTA Banner */}
      <CTABanner />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="font-chivo text-5xl md:text-6xl font-black tracking-tight leading-none text-white mb-4">
              About Aussie Inspections
            </h1>
            <p className="text-xl text-blue-100">
              Darwin's trusted building and property inspection specialists
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-chivo text-4xl font-bold text-slate-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Aussie Inspections was founded with a clear mission: to provide Darwin, Palmerston and NT's property owners with straightforward, reliable visual inspection services they can trust.
                </p>
                <p>
                  Since launching, we've completed many inspections across the Northern Territory. Our focus is simple - we document visible issues only, with no structural opinions or grey areas. This approach reduces renegotiations and protects deal flow for sellers, buyers, and agents.
                </p>
                <p>
                  We understand the Darwin property market and deliver what matters most: clear reports, fast turnaround (72 hours), flat-rate pricing from $260 incl. GST, and digital reports with photos that are easy to understand.
                </p>
                <p>
                  For specialised inspections like pest/termite and electrical, we work with trusted partners to ensure you receive quality insured reports from qualified professionals.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/assets/img/building-status-report.jpg"
                alt="Building inspector"
                className="rounded-md shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-chivo text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Qualifications & Accreditations
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our inspectors hold all required licenses and certifications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Qualified Inspectors",
                description: "Fully qualified and experienced building inspectors"
              },
              {
                title: "AS4349.1 Aligned",
                description: "Reports aligned with Australian Standards where applicable"
              },
              {
                title: "Fully Insured",
                description: "Professional indemnity and public liability insurance"
              },
              {
                title: "Partner Network",
                description: "Trusted partners for pest/termite and electrical inspections"
              },
              {
                title: "Digital Reporting",
                description: "Clear digital reports with photos, delivered via email"
              },
              {
                title: "Local Expertise",
                description: "Darwin and NT specialists who understand the local market"
              }
            ].map((qual, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 p-8 rounded-lg border border-slate-200"
              >
                <h3 className="font-chivo text-xl font-bold text-slate-900 mb-3">
                  {qual.title}
                </h3>
                <p className="text-slate-600">{qual.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
